import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Camera, Instagram, Twitter, Youtube, Video, Upload, X, Loader2, User } from 'lucide-react';

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

export default function PlayerProfileEditModal({ open, onOpenChange, profile, onSuccess, initialTab = 'details' }) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [jerseyNumber, setJerseyNumber] = useState('');
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  const [profilePhotoPublicId, setProfilePhotoPublicId] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [twitterUrl, setTwitterUrl] = useState('');
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [highlightVideo1, setHighlightVideo1] = useState('');
  const [highlightVideo2, setHighlightVideo2] = useState('');
  const [highlightVideo3, setHighlightVideo3] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (profile) {
      setJerseyNumber(profile.jersey_number || '');
      setProfilePhotoUrl(profile.profile_photo_url || '');
      setProfilePhotoPublicId(profile.profile_photo_public_id || '');
      setInstagramUrl(profile.instagram_url || '');
      setTwitterUrl(profile.twitter_url || '');
      setYoutubeUrl(profile.youtube_url || '');
      setHighlightVideo1(profile.highlight_video_url_1 || '');
      setHighlightVideo2(profile.highlight_video_url_2 || '');
      setHighlightVideo3(profile.highlight_video_url_3 || '');
      setPreviewUrl(null);
    }
  }, [profile, open]);

  // Update active tab when initialTab changes
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab, open]);

  // Generate optimized avatar URL from Cloudinary
  const getOptimizedAvatarUrl = (url) => {
    if (!url) return null;
    // If it's a Cloudinary URL, add transformations for 256x256 avatar
    if (url.includes('cloudinary.com') && url.includes('/upload/')) {
      return url.replace('/upload/', '/upload/w_256,h_256,c_fill,g_face,q_auto,f_auto/');
    }
    return url;
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please select a JPEG, PNG, or WebP image');
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      toast.error('Image must be smaller than 2MB');
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => setPreviewUrl(e.target.result);
    reader.readAsDataURL(file);

    // Upload to Cloudinary
    setUploading(true);
    try {
      // Get signature from backend
      const sigResponse = await api.get('/cloudinary/signature');
      const sig = sigResponse.data;

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      formData.append('eager', sig.eager);
      formData.append('overwrite', 'true');

      const cloudinaryResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!cloudinaryResponse.ok) {
        throw new Error('Upload failed');
      }

      const result = await cloudinaryResponse.json();
      
      // Use the eager transformation URL if available, otherwise use secure_url
      const uploadedUrl = result.eager?.[0]?.secure_url || result.secure_url;
      setProfilePhotoUrl(uploadedUrl);
      setProfilePhotoPublicId(result.public_id);
      toast.success('Photo uploaded!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload photo');
      setPreviewUrl(null);
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = () => {
    setProfilePhotoUrl('');
    setProfilePhotoPublicId('');
    setPreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const updateData = {
        jersey_number: jerseyNumber || null,
        profile_photo_url: profilePhotoUrl || null,
        profile_photo_public_id: profilePhotoPublicId || null,
        instagram_url: instagramUrl || null,
        twitter_url: twitterUrl || null,
        youtube_url: youtubeUrl || null,
        highlight_video_url_1: highlightVideo1 || null,
        highlight_video_url_2: highlightVideo2 || null,
        highlight_video_url_3: highlightVideo3 || null,
      };

      await api.patch(`/player/profile/${profile.player_id}`, updateData);
      toast.success('Profile updated!');
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const displayPhoto = previewUrl || getOptimizedAvatarUrl(profilePhotoUrl);
  const activeVideosCount = [highlightVideo1, highlightVideo2, highlightVideo3].filter(v => v?.trim()).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="player-profile-edit-modal" className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-xl">
            {activeTab === 'details' ? 'Edit Profile' : 'Edit Socials & Highlights'}
          </DialogTitle>
        </DialogHeader>

        {/* Tab Buttons */}
        <div className="flex gap-2 border-b border-slate-200 pb-3 -mt-2">
          <button
            type="button"
            onClick={() => setActiveTab('details')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'details' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Profile Details
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('social')}
            className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
              activeTab === 'social' 
                ? 'bg-slate-900 text-white' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            Socials & Highlights
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Profile Details Tab */}
          {activeTab === 'details' && (
            <>
          {/* Profile Photo Upload */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-2 block">
              Profile Photo
            </Label>
            <div className="flex items-center gap-4">
              {/* Photo preview */}
              <div className="relative">
                {displayPhoto ? (
                  <img 
                    src={displayPhoto} 
                    alt="Profile" 
                    className="w-20 h-20 rounded-full object-cover border-2 border-slate-200"
                  />
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center border-2 border-slate-200">
                    <User className="w-8 h-8 text-slate-400" />
                  </div>
                )}
                {uploading && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center">
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  </div>
                )}
              </div>
              
              {/* Upload controls */}
              <div className="flex flex-col gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="photo-file-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-xs"
                >
                  <Upload className="w-3.5 h-3.5 mr-1.5" />
                  {profilePhotoUrl ? 'Change Photo' : 'Upload Photo'}
                </Button>
                {profilePhotoUrl && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={handleRemovePhoto}
                    className="text-xs text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <X className="w-3.5 h-3.5 mr-1" />
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1.5">
              JPEG, PNG, or WebP. Max 2MB.
            </p>
          </div>

          {/* Jersey Number */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Jersey Number
            </Label>
            <Input
              value={jerseyNumber}
              onChange={(e) => setJerseyNumber(e.target.value)}
              placeholder="23"
              className="h-10"
              data-testid="jersey-number-input"
            />
          </div>
            </>
          )}

          {/* Socials & Highlights Tab */}
          {activeTab === 'social' && (
            <>
          {/* Social Links */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">
              Social Links
            </Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Instagram className="w-4 h-4 text-pink-500 flex-shrink-0" />
                <Input
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  placeholder="https://instagram.com/username"
                  className="h-9 text-sm"
                  data-testid="instagram-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Twitter className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <Input
                  value={twitterUrl}
                  onChange={(e) => setTwitterUrl(e.target.value)}
                  placeholder="https://twitter.com/username"
                  className="h-9 text-sm"
                  data-testid="twitter-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Youtube className="w-4 h-4 text-red-500 flex-shrink-0" />
                <Input
                  value={youtubeUrl}
                  onChange={(e) => setYoutubeUrl(e.target.value)}
                  placeholder="https://youtube.com/@channel"
                  className="h-9 text-sm"
                  data-testid="youtube-input"
                />
              </div>
            </div>
          </div>

          {/* Highlight Videos */}
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600 mb-1.5 block">
              <Video className="w-3.5 h-3.5 inline mr-1" />
              Highlight Videos ({activeVideosCount}/3)
            </Label>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-4">1.</span>
                <Input
                  value={highlightVideo1}
                  onChange={(e) => setHighlightVideo1(e.target.value)}
                  placeholder="YouTube, Hudl, or video URL"
                  className="h-9 text-sm"
                  data-testid="highlight-video-1-input"
                />
                {highlightVideo1 && (
                  <button
                    type="button"
                    onClick={() => setHighlightVideo1('')}
                    className="p-1 text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-4">2.</span>
                <Input
                  value={highlightVideo2}
                  onChange={(e) => setHighlightVideo2(e.target.value)}
                  placeholder="YouTube, Hudl, or video URL"
                  className="h-9 text-sm"
                  data-testid="highlight-video-2-input"
                />
                {highlightVideo2 && (
                  <button
                    type="button"
                    onClick={() => setHighlightVideo2('')}
                    className="p-1 text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 w-4">3.</span>
                <Input
                  value={highlightVideo3}
                  onChange={(e) => setHighlightVideo3(e.target.value)}
                  placeholder="YouTube, Hudl, or video URL"
                  className="h-9 text-sm"
                  data-testid="highlight-video-3-input"
                />
                {highlightVideo3 && (
                  <button
                    type="button"
                    onClick={() => setHighlightVideo3('')}
                    className="p-1 text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
            </>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || uploading}
              className="flex-1 bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="save-player-profile-button"
            >
              {saving ? 'Saving...' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
